import { useState } from "react";
import { Link, useSearch } from "wouter";
import { LockKeyhole, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function ForgotPassword() {
  const { t } = useI18n();
  // [23] Pre-fill the email carried over from the login page.
  const search = useSearch();
  const [email, setEmail] = useState(() => new URLSearchParams(search).get("email") || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err: any) { setError(err?.message || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  }

  return (
    <div className="relative isolate grid min-h-[80vh] place-items-center overflow-hidden bg-paper px-4 py-10 sm:py-14">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <img src="/recovery-airplane-clouds.webp" alt="" width="1536" height="1024" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-paper/40 via-transparent to-paper/30" />
      </div>
      <section aria-labelledby="recovery-heading" className="w-full max-w-[480px] rounded-3xl border border-white/80 bg-white p-5 shadow-[0_16px_60px_-20px_rgba(15,37,64,0.25)] sm:p-8">
        <div className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-full bg-paper" aria-hidden="true">
          <div className="grid h-14 w-14 place-items-center rounded-full border border-line bg-white shadow-sm">
            <LockKeyhole className="h-6 w-6 text-navy" />
          </div>
        </div>
        <h1 id="recovery-heading" className="mb-2 text-center text-2xl font-bold leading-tight tracking-tight text-navy sm:text-[28px]">Reset password</h1>
        <p className="mx-auto max-w-[340px] text-center text-sm leading-6 text-mut">Enter your email to receive a password reset link.</p>

        {sent ? (
          <div className="mt-6 space-y-4">
            <div role="status" className="rounded-lg bg-brand-green/10 text-brand-greendark text-sm leading-6 p-4 text-center">
              Check your inbox (and spam folder) for a password-reset link if an account exists for <span className="font-semibold">{email}</span>. If it doesn't arrive, try again later.
            </div>
            <Link href="/login" className="btn-outline min-h-12 w-full">Back to sign in</Link>
          </div>
        ) : (
          <form className="mt-6 space-y-5" onSubmit={submit}>
            <div><label htmlFor="recovery-email" className="mb-2 block text-[13px] font-semibold text-navy">{t("auth.emailAddress", "Email")} <span aria-hidden="true" className="text-brand-red">*</span></label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-mut" aria-hidden="true" />
                <input id="recovery-email" required type="email" className="field min-h-12 pl-10 text-base" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            {error && <p role="alert" className="rounded-lg border border-brand-red/20 bg-brand-red/5 p-3 text-sm leading-5 text-brand-red">{error}</p>}
            <button type="submit" disabled={busy} className="btn-red min-h-12 w-full text-sm font-semibold disabled:opacity-60">{busy ? "Sending…" : "Send reset link"}</button>
            <p className="border-t border-line pt-5 text-center text-[13px] leading-6 text-mut">Remembered it? <Link href="/login" className="font-semibold text-navy hover:underline">Sign in</Link></p>
          </form>
        )}
      </section>
    </div>
  );
}
